import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('destack_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('destack_token');
      localStorage.removeItem('destack_user');
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email, channel = 'email') => api.post('/auth/forgot-password', { email, channel }),
  verifyResetCode: (email, code) => api.post('/auth/verify-reset-code', { email, code }),
  resetPassword: (email, code, new_password) => api.post('/auth/reset-password', { email, code, new_password }),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
};

// Categories API
export const categoriesAPI = {
  list: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Stores API
export const storesAPI = {
  list: (params) => api.get('/stores', { params }),
  get: (id) => api.get(`/stores/${id}`),
  getInstitutional: (id) => api.get(`/stores/${id}/institutional`),
  create: (data) => api.post('/stores', data),
  update: (id, data) => api.put(`/stores/${id}`, data),
  delete: (id) => api.delete(`/stores/${id}`),
};

// Products API
export const productsAPI = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  getRelated: (id, limit = 8) => api.get(`/products/${id}/related`, { params: { limit } }),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Orders API
export const ordersAPI = {
  list: (params) => api.get('/orders', { params }),
  create: (data) => api.post('/orders', data),
  getByProtocol: (protocol) => api.get(`/orders/protocol/${protocol}`),
  patchStatus: (id, data) => api.patch(`/orders/${id}`, data),
  updateStatus: (id, status) => api.put(`/orders/${id}/status`, null, { params: { status } }),
  getMyOrders: () => api.get('/orders/my'),
};

// Reviews API (Etapa 3)
export const reviewsAPI = {
  create: (data) => api.post('/reviews', data),
  list: (targetType, targetId, params = {}) =>
    api.get('/reviews', { params: { target_type: targetType, target_id: targetId, ...params } }),
  summary: (targetType, targetId) =>
    api.get('/reviews/summary', { params: { target_type: targetType, target_id: targetId } }),
  bulkSummary: (targets) => api.post('/reviews/bulk-summary', { targets }),
  reviewContext: (protocol) => api.get(`/orders/protocol/${encodeURIComponent(protocol)}/review-context`),
  adminInvite: (orderId) => api.get(`/admin/orders/${orderId}/review-invite`),
  scheduleInvite: (orderId, hours = 24) => api.post(`/admin/orders/${orderId}/schedule-invite`, null, { params: { hours } }),
  processQueue: () => api.post('/admin/orders/process-invite-queue'),
  topRated: (limit = 8, minReviews = 3) => api.get('/marketplace/top-rated', { params: { limit, min_reviews: minReviews } }),
  adminList: (params = {}) => api.get('/admin/reviews', { params }),
  moderate: (id, data) => api.put(`/admin/reviews/${id}/moderate`, data),
  delete: (id) => api.delete(`/admin/reviews/${id}`),
};

// Showcase API (Etapa 4 — Vitrine Dinâmica)
export const showcaseAPI = {
  getDynamic: (limit = 30) => api.get('/showcase/dynamic', { params: { limit } }),
  adminList: () => api.get('/admin/showcase'),
  create: (data) => api.post('/admin/showcase', data),
  update: (id, data) => api.put(`/admin/showcase/${id}`, data),
  reorder: (orderIds) => api.put('/admin/showcase/reorder', { order: orderIds }),
  delete: (id) => api.delete(`/admin/showcase/${id}`),
};

// Banners API
export const bannersAPI = {
  list: () => api.get('/banners'),
  create: (data) => api.post('/banners', data),
  delete: (id) => api.delete(`/banners/${id}`),
};

// Media API
export const mediaAPI = {
  getSignature: (resourceType, folder) => 
    api.get('/cloudinary/signature', { params: { resource_type: resourceType, folder } }),
  list: (entityId) => api.get(`/media/${entityId}`),
  create: (data) => api.post('/media', data),
  delete: (id) => api.delete(`/media/${id}`),
};

// Transactions API
export const transactionsAPI = {
  list: (params) => api.get('/transactions', { params }),
  create: (data) => api.post('/transactions', data),
  delete: (id) => api.delete(`/transactions/${id}`),
  getSummary: (params) => api.get('/financial/summary', { params }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getStoresRanking: (params) => api.get('/reports/ranking/stores', { params }),
  getProductsRanking: (params) => api.get('/reports/ranking/products', { params }),
  getMonthlyOrders: (params) => api.get('/reports/orders/monthly', { params }),
  premium: (days = 30) => api.get('/dashboard/premium', { params: { days } }),
};

// Marketplace API
export const utilsAPI = {
  lookupCEP: (cep) => api.get(`/utils/cep/${(cep || '').replace(/\D/g, '')}`),
};

export const marketplaceAPI = {
  getHome: () => api.get('/marketplace/home'),
  getStats: () => api.get('/marketplace/stats'),
  getStoresWithProducts: (limit = 20, per_store = 12) => api.get('/marketplace/stores-with-products', { params: { limit, per_store } }),
  search: (q, type) => api.get('/marketplace/search', { params: { q, type } }),
};

// Settings API
export const settingsAPI = {
  getFooter: () => api.get('/settings/footer'),
  updateFooter: (data) => api.put('/settings/footer', data),
};

// AI API
export const aiAPI = {
  chat: (session_id, message, extra = {}) =>
    api.post('/ai/chat', { session_id, message, ...extra }),
  history: (session_id) => api.get(`/ai/chat/history/${session_id}`),
  generateDescription: (data) => api.post('/ai/generate-description', data),
  smartSearch: (query, limit = 12) => api.post('/ai/smart-search', { query, limit }),
};

// IA DESTACK Vendas (Admin only)
export const adminAiAPI = {
  chat: (session_id, message) => api.post('/admin/ai/chat', { session_id, message }),
  history: (session_id) => api.get(`/admin/ai/chat/history/${session_id}`),
  content: (data) => api.post('/admin/ai/content', data),
  insights: (days = 30) => api.get('/admin/ai/insights', { params: { days } }),
};

// Geo API
export const geoAPI = {
  geocode: (data) => api.post('/geocode', data),
  nearby: (lat, lng, radius_km = 50, limit = 50) =>
    api.post('/stores/nearby', { lat, lng, radius_km, limit }),
};

// Export API
export const exportAPI = {
  transactions: (params) => api.get('/export/transactions', { params, responseType: 'blob' }),
  stores: (params) => api.get('/export/stores', { params, responseType: 'blob' }),
  orders: (params) => api.get('/export/orders', { params, responseType: 'blob' }),
  ordersPdf: (params) => api.get('/export/orders/pdf', { params, responseType: 'blob' }),
};

// Upload to Cloudinary
export const uploadToCloudinary = async (file, resourceType = 'image', folder = 'stores') => {
  const { data: sig } = await mediaAPI.getSignature(resourceType, folder);
  
  const formData = new FormData();
  formData.append('file', file);
  formData.append('api_key', sig.api_key);
  formData.append('timestamp', sig.timestamp);
  formData.append('signature', sig.signature);
  formData.append('folder', sig.folder);
  
  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
    { method: 'POST', body: formData }
  );
  
  return response.json();
};

// Download helper
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export default api;
